import { Path } from 'slate';
import { defineTransform } from '../transform';

import {
  adjustPath,
  pathWasRemoved,
  textOpBounds,
  adjustNodePathForNodeMove,
  adjustPathForNodeSplit,
  adjustPositionForNodeSplit,
  adjustPathForNodeMerge,
  adjustPositionForNodeMerge,
  adjustPathForNodeInsert,
  adjustPathForNodeRemove,
} from '../utils';

defineTransform('remove_text', 'insert_text', (op1, op2, side) => {
  if (!Path.equals(op1.path, op2.path)) return op1;
  const bounds1 = textOpBounds(op1);
  const bounds2 = textOpBounds(op2);
  if (bounds1.end < bounds2.start) return op1;
  let ops = [];
  let slicedText = op1.text;

  if (bounds1.start < bounds2.start) {
    ops = ops.concat([
      { ...op1, text: op1.text.slice(0, bounds2.start - bounds1.start) },
    ]);
    slicedText = op1.text.slice(bounds2.start - bounds1.start);
  }

  ops = ops.concat([
    {
      ...op1,
      offset: op1.offset + op2.text.length,
      text: slicedText,
    },
  ]);

  return ops;
});

defineTransform('remove_text', 'remove_text', (op1, op2, side) => {
  if (!Path.equals(op1.path, op2.path)) return op1;
  const bounds1 = textOpBounds(op1);
  const bounds2 = textOpBounds(op2);

  if (bounds1.end < bounds2.start) return op1;
  if (bounds1.start >= bounds2.end) {
    return {
      ...op1,
      offset: op1.offset - op2.text.length,
    };
  }

  // Handle overlapping ranges
  const tOp = {
    ...op1,
    text: '',
  };

  // Slice text off the beginning that wasn't deleted by bounds2
  if (bounds1.start < bounds2.start) {
    tOp.text = op1.text.slice(0, bounds2.start - bounds1.start);
  }

  // Slice text off the end that wasn't deleted by bounds2
  if (bounds1.end > bounds2.end) {
    tOp.text += op1.text.slice(bounds2.end - bounds1.start);
  }

  // Adjust the offset
  if (bounds1.start > bounds2.start) {
    tOp.offset = bounds2.start;
  }
  return tOp;
});

defineTransform('remove_text', 'split_node', (op1, op2, side) => {
  const tOp = { ...op1 };
  if (Path.equals(op1.path, op2.path)) {
    const bounds1 = textOpBounds(op1);

    // Our delete spans the split, so we need to transform it into
    // two operations, one on either side of the split.
    if (
      (bounds1.start < op2.position ||
        (bounds1.start === op2.position && side === 'right')) &&
      bounds1.end > op2.position
    ) {
      const leftText = op1.text.slice(0, op2.position - bounds1.start);
      const rightText = op1.text.slice(op2.position - bounds1.start);
      const leftOp = { ...op1, text: leftText };
      const rightOp = {
        ...op1,
        path: adjustPath(op1.path, 1, op1.path.length - 1),
        offset: 0,
        text: rightText,
      };

      return [leftOp, rightOp];
    }
  }

  tOp.path = adjustPathForNodeSplit(op1.path, op1.offset, op2, side);
  tOp.offset = adjustPositionForNodeSplit(
    op1.path,
    op1.offset,
    op2,
    side === 'left'
  );

  return tOp;
});

defineTransform('remove_text', 'merge_node', (op1, op2, side) => {
  return {
    ...op1,
    path: adjustPathForNodeMerge(op1.path, op2),
    offset: adjustPositionForNodeMerge(op1.path, op1.offset, op2),
  };
});

defineTransform('remove_text', 'move_node', (op1, op2, side) => {
  return {
    ...op1,
    path: adjustNodePathForNodeMove(op1.path, op2),
  };
});

defineTransform('remove_text', 'insert_node', (op1, op2, side) => {
  return {
    ...op1,
    path: adjustPathForNodeInsert(op1.path, op2, true),
  };
});

defineTransform('remove_text', 'remove_node', (op1, op2, side) => {
  if (pathWasRemoved(op1.path, op2.path)) return;

  return {
    ...op1,
    path: adjustPathForNodeRemove(op1.path, op2, true),
  };
});

defineTransform('remove_text', 'set_node', (op1, op2, side) => op1);
defineTransform('remove_text', 'add_mark', (op1, op2, side) => op1);
defineTransform('remove_text', 'remove_mark', (op1, op2, side) => op1);
defineTransform('remove_text', 'set_mark', (op1, op2, side) => op1);
defineTransform('remove_text', 'add_annotation', (op1, op2, side) => op1);
defineTransform('remove_text', 'remove_annotation', (op1, op2, side) => op1);
defineTransform('remove_text', 'set_annotation', (op1, op2, side) => op1);
defineTransform('remove_text', 'set_selection', (op1, op2, side) => op1);
defineTransform('remove_text', 'add_metadata', (op1, op2, side) => op1);
defineTransform('remove_text', 'remove_metadata', (op1, op2, side) => op1);
defineTransform('remove_text', 'set_metadata', (op1, op2, side) => op1);
